import * as Sentry from "@sentry/vue";
import router from "@/app/router";
import type { App } from "vue";
import { isFirebaseAuthNetworkRequestFailed } from "@/shared/utils/firebaseAuthError.utils";

const DEFAULT_SENTRY_DSN =
  "https://69cce73205e2e6e8e58bfecea77180be@o4511299480125440.ingest.us.sentry.io/4511299497361408";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN || DEFAULT_SENTRY_DSN;
const sentryEnabled = import.meta.env.PROD || import.meta.env.VITE_SENTRY_ENABLED === "true";
const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;

function getSentrySampleRate(value: string | undefined, fallback: number) {
  const sampleRate = Number.parseFloat(value || "");

  if (!Number.isFinite(sampleRate)) {
    return fallback;
  }

  return Math.min(Math.max(sampleRate, 0), 1);
}

const sentrySessionSampleRate = getSentrySampleRate(
  import.meta.env.VITE_SENTRY_SESSION_SAMPLE_RATE,
  0,
);
const sentryReplayOnErrorSampleRate = sentrySessionSampleRate > 0 ? 1.0 : 0;

export function initSentry(app: App<Element>) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    enabled: sentryEnabled,
    environment: sentryEnvironment,
    release: import.meta.env.APP_VERSION,
    enableLogs: true,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
      Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
    ],
    sendDefaultPii: true,
    replaysSessionSampleRate: sentrySessionSampleRate,
    replaysOnErrorSampleRate: sentryReplayOnErrorSampleRate,
    beforeSend(event, hint) {
      if (isFirebaseAuthNetworkRequestFailed(hint.originalException)) {
        return null;
      }

      return event;
    },
  });
}
